import { select, call, put, takeLatest } from 'redux-saga/effects';
import * as http from 'utils/http';
import config from 'config';
import history from 'modules/history';

import {
  getDurationFromMinutes,
  getTravelTime,
  getTravelTimeGivenInForm,
  getNumberOfPallets,
  getPickupDurationByNumberOfItems,
  getTravelTimeNeeded,
  computeDefaultOrderWithDefaultTimes,
} from './utils';

import {
  setShipperUsers,
  setShipperAddresses,
  setMainLoading,
  setSubmiting,
} from 'redux/modules/shipper-order-form/duck';

import { fetchAddresses } from 'redux/modules/addresses/effects';

import { adaptShipperOrder, setUserSettings } from 'redux/modules/shipper-order-form/utils';

import { computeShipperOrderForRepeating } from './repeated-orders';

import { displayError } from 'redux/modules/error-handler/effects';
import { SHIPPERORDER_CREATE, HANDLING_NOT_ALLOWED } from 'redux/modules/error-handler/constants';
import { isHandling400Error } from 'redux/modules/error-handler/utils';

import {
  setOpened,
  setCurrentShipperOrder,
  setRecentlyCreatedShipperOrder,
  setAppendCurrentAddresses,
  setCurrentAddresses,
  LOCALSTORAGE_ITEM,
  LOCALSTORAGE_ADDRESSES,
  setSavingCost,
  setCheckTimes,
  setTimeWarning,
  setDeleteTimeWarning,
  setShipperWarning,
  setDeleteShipperWarning,
} from './duck';

import {
  getCurrentShipperOrder,
  isModalOpened,
  getRecentlyCreatedShipperOrder,
  getCurrentAddresses,
  getCheckTimes,
} from './selectors';

import {
  formChange$,
  addAddress$,
  clearForm$,
  clearForm,
} from 'redux/modules/shipper-order-form/effects';

import {
  baseShipperOrder,
  RETURN_EXTRA,
  MODIFIED_PRICE_EXTRA,
} from 'modules/shipper-order/Form/constants';

import { openShipperOrderDetail } from 'redux/modules/shipper-orders/effects';
import { getMe } from 'redux/modules/job-detail/selectors';
import {
  isPreferencePresent,
  DONT_APPLY_SHIPPER_VALIDATION,
} from 'modules/preferences-manager/preferences';
import { getQuote } from '../shipper-order-form/selectors';

const toggleModal$ = 'shipper-order-new/toggleModal$';
const submitForm$ = 'shipper-order-new/submitForm$';
const initForm$ = 'shipper-order-new/initForm$';
const openFormWithShipperOrder$ = 'shipper-order-new/openFormWithShipperOrder$';
const openFormWithQuoteValues$ = 'shipper-order-new/openFormWithQuoteValues$';

const saveCost$ = 'shipper-order-new/saveCost$';
const saveCostLater$ = 'shipper-order-new/saveCostLater$';

export const submitForm = (shipperOrder) => ({
  type: submitForm$,
  payload: {
    shipperOrder,
  },
});

export const openFormWithShipperOrder = ({ id }) => ({
  type: openFormWithShipperOrder$,
  payload: {
    id,
  },
});

export const openFormWithQuoteValues = (defaultValues) => ({
  type: openFormWithQuoteValues$,
  payload: {
    ...defaultValues,
  },
});

export const initForm = (shipperOrder) => ({
  type: initForm$,
  payload: {
    shipperOrder,
  },
});

export const toggleModal = (opened = true) => ({
  type: toggleModal$,
  payload: {
    opened,
  },
});

export const saveCost = ({ cost }) => ({
  type: saveCost$,
  payload: {
    cost,
  },
});

export const saveCostLater = () => ({
  type: saveCostLater$,
  payload: {},
});

function* handleModal({ payload }) {
  yield put(setOpened(payload.opened));
  yield put(setDeleteTimeWarning());
  yield put(setDeleteShipperWarning());
}

function* handleValidateShipper(shipper) {
  const me = yield select(getMe);
  try {
    const stopValidation = isPreferencePresent(me, DONT_APPLY_SHIPPER_VALIDATION);

    if (stopValidation) return false;

    yield put(setDeleteShipperWarning());

    if (!shipper) return;

    const data = yield call(http.get, `${config.BASE_URL}/api/shippers/${shipper?.id}/`);

    const isCreditCard = ['CARD'].includes(data?.payment_method);
    const isNotValidated = ['PENDING_VALIDATION'].includes(data?.payment_method_validation_status);

    if (!isCreditCard && !isNotValidated) return false;

    yield put(
      setShipperWarning({
        id: shipper?.id,
        isCreditCard,
        isNotValidated,
      })
    );
    return true;
  } catch (e) {
    console.error(e);
  }
}

function* handleSubmit({ payload }) {
  const { shipperOrder } = payload;
  try {
    const isInvalid = yield call(handleValidateShipper, shipperOrder?.shipper);

    if (isInvalid) return;

    const lastQuote = yield select(getQuote);
    yield put(setSubmiting(true));

    const adaptedShipperOrder = yield call(adaptShipperOrder, {
      ...shipperOrder,
      ...(lastQuote?.slug ? { quote_slug: lastQuote.slug } : {}),
    });
    const newShipperOrder = yield call(
      http.post,
      `${config.API_URL}mercury/shipper_order_edits/`,
      adaptedShipperOrder
    );

    yield put(clearForm());

    if ((newShipperOrder.journeys || []).length) {
      const journey = newShipperOrder.journeys[0];
      const hasModifiedOrReturnExtra = !!newShipperOrder?.extra_prices?.find(
        ({ concept }) => concept === MODIFIED_PRICE_EXTRA || concept === RETURN_EXTRA
      );
      const showManualCostModal =
        journey?.is_manual || journey?.is_tender || hasModifiedOrReturnExtra;

      if (showManualCostModal) {
        yield put(setRecentlyCreatedShipperOrder(newShipperOrder));
        return;
      }
    }

    yield put(openShipperOrderDetail({ id: newShipperOrder.id }));
  } catch (err) {
    const errorDetails = yield call(http.getError, err);
    if (isHandling400Error(errorDetails)) {
      yield put(displayError(err, HANDLING_NOT_ALLOWED));
    } else {
      yield put(displayError(err, SHIPPERORDER_CREATE));
    }
  } finally {
    yield put(setSubmiting(false));
  }
}

function* handleFormInit() {
  try {
    yield put(setDeleteShipperWarning());
    yield put(setMainLoading(true));

    const shipperOrder = yield select(getCurrentShipperOrder);

    // If current shipperOrder has ID handles a repeated_shipperOrder
    const { id } = shipperOrder;
    if (id) {
      const newShipperOrder = yield call(
        http.get,
        `${config.API_URL}mercury/shipper_order_edits/${id}/`
      );

      yield put(setCurrentShipperOrder(computeShipperOrderForRepeating(newShipperOrder)));
    }

    const currentShipperorder = yield select(getCurrentShipperOrder);

    const { shipper, user } = currentShipperorder;

    if (!shipper) {
      return;
    }

    const { users = [] } = shipper;

    yield put(setShipperUsers(users));

    const addresses = yield call(fetchAddresses, shipper);
    yield put(setShipperAddresses(addresses));

    if (user) {
      yield call(setUserSettings, user);
    }
  } catch (err) {
    yield call(http.getError, err);
  } finally {
    yield put(setMainLoading(false));
  }
}

const saveValues = (values) =>
  new Promise((resolve) => {
    localStorage.setItem(LOCALSTORAGE_ITEM, JSON.stringify(values));
    resolve(values);
  });

const saveAddress = (values) =>
  new Promise((resolve) => {
    localStorage.setItem(LOCALSTORAGE_ADDRESSES, JSON.stringify(values));
    resolve(values);
  });

function* handleFormSaving({ payload }) {
  const { values } = payload;
  try {
    yield put(setDeleteShipperWarning());
    const isOpened = yield select(isModalOpened);
    const checkTimes = yield select(getCheckTimes);

    if (!isOpened) return;

    yield call(saveValues, values);
    yield put(setCurrentShipperOrder(values));

    if (checkTimes) yield call(handleValidateHoursArePossible);
  } catch (err) {
    console.error(err);
  }
}

function* handleCostSaving({ payload }) {
  const { cost } = payload;
  try {
    const shipperOrder = yield select(getRecentlyCreatedShipperOrder);
    const journey = (shipperOrder.journeys || [])[0];

    if (!journey) return;

    yield put(setSavingCost(true));
    yield call(http.patch, journey.url, {
      fixed_cost_for_journey: cost,
    });

    yield put(openShipperOrderDetail({ id: shipperOrder.id }));
  } catch (err) {
    console.error(err);
    yield call(http.getError, err);
  } finally {
    yield put(setSavingCost(false));
    yield put(setRecentlyCreatedShipperOrder(null));
  }
}

function* handleSaveCostLater() {
  const shipperOrder = yield select(getRecentlyCreatedShipperOrder);
  yield put(openShipperOrderDetail({ id: shipperOrder.id }));
  yield put(setRecentlyCreatedShipperOrder(null));
}

function* handleSaveAddress({ payload }) {
  const { address } = payload;
  const currentAddresses = yield select(getCurrentAddresses);

  yield put(setAppendCurrentAddresses([address]));
  yield call(saveAddress, [...currentAddresses, address]);
}

function* handleClear() {
  const shipperOrder = baseShipperOrder();
  yield call(saveValues, shipperOrder);
  yield put(setCurrentShipperOrder(shipperOrder));
  yield put(setCurrentAddresses([]));
  yield call(saveAddress, []);
}

function* handleRepeatShipperOrder({ payload }) {
  const { id } = payload;
  yield put(setMainLoading(true));
  yield put(setCurrentShipperOrder({ id }));
  yield call(history.push, '/new_shipper_order');
}

/**
 * Opens form with default values from Quote (shipper and date)
 * @param payload.shipper {Object}
 * @param payload.date {String}   (SAME_DAY, NEXT_DAY, MORE_THAN_2_DAYS)
 */
function* handleCreateShipperOrderFromQuote({ payload }) {
  const { shipper, date, spotLaneId } = payload;

  const defaultOrder = computeDefaultOrderWithDefaultTimes({ order: baseShipperOrder(), date });
  const shipperObject = yield call(http.get, `${config.BASE_URL}/api/shippers/${shipper?.id}/`);
  const users = yield shipperObject?.user_set.map((userUrl) => call(http.get, userUrl));

  const shipperWithUsers = {
    ...shipperObject,
    users,
  };

  yield put(
    setCurrentShipperOrder({
      ...defaultOrder,
      shipper: shipperWithUsers,
      ...(!!spotLaneId ? { spot_lane: spotLaneId } : {}),
    })
  );

  yield call(history.push, '/new_shipper_order');
}

function* handleValidateHoursArePossible() {
  try {
    const { shipments } = yield select(getCurrentShipperOrder);
    yield put(setDeleteTimeWarning());

    for (let [i, shipment] of shipments.entries()) {
      const {
        loads,
        address_from: LOCATION_START,
        address_to: LOCATION_FINISH,
        pickup_time_from: TIME_START,
        dropoff_time_to: TIME_FINISH,
      } = shipment;

      if (!LOCATION_START || !LOCATION_FINISH) return;

      const numberOfPallets = getNumberOfPallets(loads);

      const timeInPickup = getPickupDurationByNumberOfItems(numberOfPallets);
      const timeTravelling = yield call(getTravelTime, LOCATION_START, LOCATION_FINISH);

      const travelTimeInForm = getTravelTimeGivenInForm({ start: TIME_START, finish: TIME_FINISH });
      const travelTimeNeeded = getTravelTimeNeeded({
        start: TIME_START,
        timeTravelling,
        timeInPickup,
      });

      if (travelTimeInForm < travelTimeNeeded) {
        yield put(
          setTimeWarning({
            stopKey: i + 1,
            givenTime: getDurationFromMinutes(travelTimeInForm),
            realTime: getDurationFromMinutes(travelTimeNeeded),
          })
        );
      }
    }

    yield put(setCheckTimes(false));
  } catch (e) {
    console.error('error', e);
  }
}

// Watchers
export function* watchShipperOrderNew() {
  yield takeLatest(toggleModal$, handleModal);
  yield takeLatest(submitForm$, handleSubmit);
  yield takeLatest(formChange$, handleFormSaving);
  yield takeLatest(initForm$, handleFormInit);
  yield takeLatest(saveCost$, handleCostSaving);
  yield takeLatest(saveCostLater$, handleSaveCostLater);
  yield takeLatest(addAddress$, handleSaveAddress);
  yield takeLatest(clearForm$, handleClear);
  yield takeLatest(openFormWithShipperOrder$, handleRepeatShipperOrder);
  yield takeLatest(openFormWithQuoteValues$, handleCreateShipperOrderFromQuote);
}

export { handleSubmit, handleValidateShipper };
